import React, { useEffect, useId, useMemo } from "react";
import { Icon } from "../../";

/**
 * Reusable Image Upload Component
 * Displays an image with an overlay containing image and dots icons
 */
const ImageUpload = ({
  image,
  onChange,
  onRemove,
  accept = "image/*",
  size = "md",
  showRemoveButton = true,
  className = "",
  alt = "Preview",
  disabled = false,
  children, // Allow custom children
  imageClassName = "", // Custom image classes
  containerClassName = "", // Custom container classes
  uploadLabel = alt,
}) => {
  const generatedInputId = useId();
  const objectUrl = useMemo(() => {
    if (!image || typeof image === "string" || !image.file) return null;
    return typeof URL.createObjectURL === "function"
      ? URL.createObjectURL(image.file)
      : null;
  }, [image]);

  useEffect(
    () => () => {
      if (objectUrl && typeof URL.revokeObjectURL === "function") {
        URL.revokeObjectURL(objectUrl);
      }
    },
    [objectUrl],
  );

  const getSizeClasses = () => {
    const sizes = {
      sm: "w-16 h-16",
      md: "w-24 h-24",
      lg: "w-32 h-32",
      xl: "w-72 h-72",
    };
    return sizes[size] || sizes.md;
  };

  const getImageSrc = () => {
    if (!image) return null;

    if (typeof image === "string") return image;
    if (image.base64) return image.base64;
    if (image.url) return image.url;
    if (image.file) return objectUrl;

    return null;
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onChange) {
      onChange(file, e);
    }
  };

  const imageSrc = getImageSrc();

  // If children are provided, render them with overlay
  if (children) {
    return (
      <div className={`relative ${containerClassName}`}>
        {children}
        {imageSrc && (
          <>
            {/* Overlay - Dots and Image Icon - Always visible */}
            <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40">
              <div className="flex items-center space-x-1">
                <Icon name="Image" className="h-4 w-4 text-white" />
                <Icon name="MoreVertical" className="h-4 w-4 text-white" />
              </div>
            </div>
            {/* Clickable overlay */}
            <input
              id={generatedInputId}
              type="file"
              accept={accept}
              aria-label={uploadLabel}
              onChange={handleFileChange}
              className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
              disabled={disabled}
            />
          </>
        )}
      </div>
    );
  }

  // Default render
  return (
    <div className={`relative inline-block ${className}`}>
      {imageSrc ? (
        <>
          <img
            src={imageSrc}
            alt={alt}
            className={`${getSizeClasses()} rounded-lg border-2 border-gray-700 object-cover ${imageClassName}`}
          />
          {/* Overlay - Dots and Image Icon - Always visible */}
          <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40">
            <div className="flex items-center space-x-1">
              <Icon name="Image" className="h-5 w-5 text-white" />
              <Icon name="MoreVertical" className="h-5 w-5 text-white" />
            </div>
          </div>
          {/* Clickable overlay */}
          <input
            id={generatedInputId}
            type="file"
            accept={accept}
            aria-label={uploadLabel}
            onChange={handleFileChange}
            className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
            disabled={disabled}
          />
        </>
      ) : (
        <div
          className={`${getSizeClasses()} relative flex items-center justify-center rounded-lg border border-gray-700 bg-gray-800`}
        >
          <Icon name="Image" className="h-8 w-8 text-gray-600" />
          <input
            id={generatedInputId}
            type="file"
            accept={accept}
            aria-label={uploadLabel}
            onChange={handleFileChange}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            disabled={disabled}
          />
        </div>
      )}
      {showRemoveButton && onRemove && imageSrc && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute -bottom-6 left-1/2 -translate-x-1/2 transform text-xs text-red-400 transition-colors hover:text-red-300"
        >
          Remove
        </button>
      )}
    </div>
  );
};

export default ImageUpload;
